import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect } from "react";
import { defaultPUR5020_R01Filter, PUR5020_R01Filter, usePUR5020_R01Store } from "src/containers/mes/PUR/PUR5020_R01/Store_PUR5020_R01";
import { PUR5020_R01HeaderDto, PUR5020_R01DetailDto } from "./Dto_PUR5020_R01";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { useToast } from "src/hooks/useToast";
import useUiuxStore from "src/store/useUiuxStore";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { downloadSettingExcel } from "src/utils/blob/blobUtil";
import { showWarning, ShowSuccessFetch } from "src/utils/toast/toastUtil";
import { gridRefSelectedRowHandler } from "src/utils/grid/GridHandlerContent";
import {
	downloadDetailExcel_PUR5020_R01,
	getPUR5020_R01AllCount,
	getPUR5020_R01List,
	getDetailPUR5020_R01List,
} from "./Api_PUR5020_R01";

interface Props {
	gridRef: React.RefObject<AgGridReact<PUR5020_R01HeaderDto> | null>;
	detailGridRef: React.RefObject<AgGridReact<PUR5020_R01DetailDto> | null>;
	setMessage: (message: string) => void;
}

export const Content_PUR5020_R01 = (props: Props) => {
	const { gridRef, detailGridRef } = props;
	const { setMessage } = useUiuxStore();
	const {
		PUR5020_R01Filter,
		PUR5020_R01PastFilter, setPUR5020_R01PastFilter,
		PUR5020_R01List, setPUR5020_R01List,
		setOriginalPUR5020_R01DetailList,
		selectedPUR5020_R01, setSelectedPUR5020_R01,
		selectedRowInfo, setSelectedRowInfo,
		setPUR5020_R01DetailList,
		initSelectedPUR5020_R01DetailList,
		allCount, setAllCount,
		initData,
		setScrollTop, setDetailScrollTop,
	} = usePUR5020_R01Store();

	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { success, error: showError } = useToast();

	useFetchActionbarAuth("PUR5020_R01");

	const formatDateLocal = (date: Date) => {
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	};

	const getParams = (filter: PUR5020_R01Filter) => {
		const startDate = filter.txDt?.from ? formatDateLocal(new Date(filter.txDt.from)) : (filter.startDate || '');
		const endDate = filter.txDt?.to ? formatDateLocal(new Date(filter.txDt.to)) : (filter.endDate || '');
		return {
			compCd: filter.compCd || '',
			logicCenter: filter.logicCenter || '',
			warehouseCd: filter.warehouseCd || '',
			brandCd: filter.brandCd || '',
			partnerCd: filter.partnerCd || '',
			itemCd: filter.itemCd || '',
			itemGroup: filter.itemGroup || '',
			startDate,
			endDate,
			searchText: filter.searchText || '',
			includeZeroStock: filter.includeZeroStock ?? false,
		};
	};

	const renewalAllCount = useCallback(async () => {
		const allCountResponse = await getPUR5020_R01AllCount(getParams(PUR5020_R01Filter));
		setAllCount(allCountResponse.data.data);
		return allCountResponse.data.data;
	}, [PUR5020_R01Filter, setAllCount]);

	const fetchDetailList = useCallback(async (logicCenter: string, warehouseCd: string, itemCd: string, itemStatus: string) => {
		initSelectedPUR5020_R01DetailList();
		setDetailScrollTop(null);
		const responseData = (
			await getDetailPUR5020_R01List(
				{ ...getParams(PUR5020_R01Filter), logicCenter, warehouseCd, itemCd, itemStatus },
				0,
			)
		).data.data;
		if (responseData && responseData.length > 0) {
			setPUR5020_R01DetailList(responseData.map((value, idx) => ({
				...value,
				valueIndex: value.valueIndex ?? idx + 1,
				orderIndex: value.orderIndex ?? idx + 1,
				orderIndexSeq: value.orderIndexSeq ?? 0,
			})));
			setOriginalPUR5020_R01DetailList(responseData);
			const selectedRowForMsg = PUR5020_R01List.find(v => v.itemCd === itemCd);
			setMessage(`${selectedRowForMsg?.itemNm || ''}의 상세내역 ${responseData.length}건을 조회하였습니다.`);
			setTimeout(() => setMessage(""), 5000);
		} else {
			showWarning('창고코드가 일치하지 않습니다.');
			setPUR5020_R01DetailList([]);
		}
	}, [PUR5020_R01Filter, setPUR5020_R01DetailList, setOriginalPUR5020_R01DetailList, setDetailScrollTop, setMessage, PUR5020_R01List, initSelectedPUR5020_R01DetailList]);

	const fetchData = useCallback(async () => {
		setScrollTop(null);
		setDetailScrollTop(null);
		setLoadingSpinner(true);
		const responseData = (await getPUR5020_R01List(
			'',
			getParams(PUR5020_R01Filter),
			PUR5020_R01List.length ?? 0
		));
		const newList = [...responseData.data.data];
		setPUR5020_R01List(newList);
		const processAllCount = await renewalAllCount();
		if (newList && newList.length > 0) {
			gridRefSelectedRowHandler<PUR5020_R01HeaderDto>(
				gridRef,
				(row) => {
					setSelectedPUR5020_R01(row?.itemCd);
					setSelectedRowInfo(row ? {
						itemCd: row.itemCd,
						warehouseCd: row.warehouseCd ?? '',
						logicCenter: row.logicCenter ?? '',
						itemStatus: row.itemStatus ?? '',
						valueIndex: Number(row.valueIndex),
					} : undefined);
				},
				() => true,
			);
			setPUR5020_R01PastFilter(PUR5020_R01Filter);
			ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
			setTimeout(() => setMessage(""), 5000);
		} else {
			showWarning('데이터가 없습니다.');
			setSelectedPUR5020_R01(undefined);
			setSelectedRowInfo(undefined);
			setPUR5020_R01List([]);
			setPUR5020_R01DetailList([]);
			setOriginalPUR5020_R01DetailList([]);
		}
		setLoadingSpinner(false);
		return responseData;
	}, [PUR5020_R01Filter, PUR5020_R01List, setPUR5020_R01List, setPUR5020_R01PastFilter, setSelectedPUR5020_R01, setSelectedRowInfo, selectedPUR5020_R01, renewalAllCount, gridRef, setMessage, setScrollTop, setDetailScrollTop]);

	useEffect(() => {
		if (selectedPUR5020_R01 && selectedRowInfo) {
			const logicCenter = selectedRowInfo.logicCenter || PUR5020_R01Filter.logicCenter || '';
			const warehouseCd = selectedRowInfo.warehouseCd || PUR5020_R01Filter.warehouseCd || '';
			const status = selectedRowInfo.itemStatus || '';
			fetchDetailList(logicCenter, warehouseCd, selectedPUR5020_R01, status);
		} else if (!selectedPUR5020_R01) {
			setPUR5020_R01DetailList([]);
		}
	}, [selectedPUR5020_R01, selectedRowInfo]);

	const handleTableRowClick = useCallback(async (_rowData: any) => {}, []);

	const initContainerData = useCallback(() => {
		initData();
		setMessage("");
	}, [initData, setMessage]);

	const handleDetailExcelClick = useCallback(async () => {
		if (!selectedPUR5020_R01 || !selectedRowInfo) {
			showWarning('상세 조회할 품목을 선택해주세요.');
			return;
		}
		setLoadingSpinner(true);
		const logicCenter = selectedRowInfo.logicCenter || PUR5020_R01Filter.logicCenter || '';
		const warehouseCd = selectedRowInfo.warehouseCd || PUR5020_R01Filter.warehouseCd || '';
		const status = selectedRowInfo.itemStatus || '';
		const params = {
			...getParams(PUR5020_R01Filter),
			logicCenter,
			warehouseCd,
			itemCd: selectedPUR5020_R01,
			itemStatus: status
		};
		const response = await downloadDetailExcel_PUR5020_R01('search', params, 0);
		downloadSettingExcel({ responseData: response.data.data as any, fileName: '재고수불장_수불이력.xlsx' });
		setLoadingSpinner(false);
	}, [selectedPUR5020_R01, selectedRowInfo, PUR5020_R01Filter, setLoadingSpinner, showWarning]);

	return {
		fetchData,
		initContainerData,
		handleDetailExcelClick,
		handleTableRowClick,
	};
};
